import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Copy, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AffiliateDashboard() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [commissions, setCommissions] = useState([]);

  useEffect(() => {
    api.get("/affiliate/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/affiliate/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/affiliate/commissions").then((r) => setCommissions(r.data)).catch(() => {});
  }, []);

  if (!summary) return <div className="overline animate-pulse">Loading…</div>;
  const profile = summary.profile;
  const refLink = profile ? `${window.location.origin}/r/${profile.referral_slug}` : null;

  return (
    <div className="space-y-10">
      {profile && (
        <div className="border border-black p-6 bg-zinc-950 text-white flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div>
            <div className="overline text-zinc-500 mb-2">Your referral link</div>
            <div className="font-mono text-sm break-all" data-testid="referral-link">{refLink}</div>
            {profile.coupon_code && (
              <div className="mt-3 text-sm">
                <span className="overline text-zinc-500 mr-3">Coupon</span>
                <span className="font-mono">{profile.coupon_code}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              className="inline-flex items-center gap-2 px-4 py-2 border border-white hover:bg-white hover:text-black transition-colors"
              onClick={() => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied"); }}
              data-testid="copy-referral"
            >
              <Copy size={14} /> Copy link
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI k="Clicks" v={summary.clicks} />
        <KPI k="Orders" v={summary.orders} />
        <KPI k="Revenue" v={money(summary.revenue_cents)} />
        <KPI k="Commission (paid)" v={money(summary.commission_paid_cents)} />
        <KPI k="Commission pending" v={money(summary.commission_pending_cents)} />
        <KPI k="Commission approved" v={money(summary.commission_approved_cents)} />
        <KPI k="Tier" v={profile?.tier || "—"} />
        <KPI k="Rate" v={profile ? `${(profile.commission_rate_bps / 100).toFixed(1)}%` : "—"} />
      </div>

      <div>
        <div className="overline mb-3">Recent referred orders</div>
        {orders.length === 0 ? (
          <div className="border border-dashed border-black/20 p-8 text-center text-zinc-600">No referred orders yet.</div>
        ) : (
          <div className="border border-black/10 divide-y divide-black/10">
            {orders.slice(0, 8).map((o) => (
              <div key={o.id} className="p-4 flex items-center justify-between" data-testid={`aff-order-${o.order_number}`}>
                <div>
                  <div className="font-mono text-sm">{o.order_number}</div>
                  <div className="text-xs text-zinc-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono">{money(o.subtotal_cents - (o.discount_cents || 0))}</div>
                  <div className="overline mt-1">{o.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="overline mb-3">Commissions</div>
        {commissions.length === 0 ? (
          <div className="border border-dashed border-black/20 p-8 text-center text-zinc-600">No commissions yet.</div>
        ) : (
          <div className="border border-black/10 divide-y divide-black/10">
            {commissions.map((c) => (
              <div key={c.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-mono text-sm">{c.order_number}</div>
                  <div className="text-xs text-zinc-500 mt-1">{new Date(c.created_at).toLocaleString()} · {(c.rate_bps / 100).toFixed(1)}%</div>
                </div>
                <div className="text-right">
                  <div className="font-mono">{money(c.amount_cents)}</div>
                  <div className="overline mt-1">{c.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ k, v }) {
  return (
    <div className="border border-black/10 p-4">
      <div className="overline">{k}</div>
      <div className="font-display text-xl font-bold mt-2">{v}</div>
    </div>
  );
}
